import { z } from 'zod';

/* ─────────── Надзор, замечания и вопросы к проекту: единый реестр (раздел 6.10) ─────────── */

const DateStr = z.iso.date();

/** Источник замечания (УЧ-01) */
export const REMARK_SOURCES = {
  tech_supervision: 'Технадзор заказчика',
  author_supervision: 'Авторский надзор',
  expertise: 'Экспертиза',
  customer: 'Заказчик',
  otk: 'ОТК',
  ohs: 'Охрана труда',
  /** Отдельный тип ПБ (ПР-07); ранее входил в «ОТ и ПБ» */
  fire_safety: 'Пожарная безопасность',
  ecology: 'Экология',
  builders: 'Строители',
} as const;
export type RemarkSource = keyof typeof REMARK_SOURCES;

/** Источники, замечания которых по умолчанию внутренние: подрядчику не видны (УЧ-07) */
export const INTERNAL_SOURCES: RemarkSource[] = ['otk', 'ohs', 'fire_safety', 'ecology'];

export const REMARK_KINDS = {
  prescription: 'Предписание',
  remark: 'Замечание',
  instruction: 'Указание',
} as const;
export type RemarkKind = keyof typeof REMARK_KINDS;

/** Статусы одни для всех источников (УЧ-01); «просрочено» — признак по сроку, а не статус */
export const REMARK_STATUSES = {
  issued: 'Выдано',
  in_work: 'В работе',
  fixed: 'Устранено, ждёт проверки',
  closed: 'Закрыто',
  returned: 'Возвращено на устранение',
  disputed: 'Оспаривается',
  cancelled: 'Отменено',
} as const;
export type RemarkStatus = keyof typeof REMARK_STATUSES;

export const OPEN_REMARK: RemarkStatus[] = ['issued', 'in_work', 'fixed', 'returned', 'disputed'];

/** Коренные причины при закрытии (УЧ-07) */
export const ROOT_CAUSES = {
  design_error: 'Ошибка проекта',
  technology: 'Нарушение технологии',
  supplier_material: 'Материал поставщика',
  sections_mismatch: 'Несогласованность разделов',
  ohs_violation: 'Нарушение ОТ',
  other: 'Другое',
} as const;
export type RootCause = keyof typeof ROOT_CAUSES;

/** Точка на листе: доли ширины и высоты (0…1) — не зависит от масштаба просмотра (ПД-10) */
const Point = z.number().min(0).max(1);

export const RemarkInput = z.object({
  projectId: z.uuid(),
  objectId: z.uuid().nullable().optional(),
  source: z.enum(Object.keys(REMARK_SOURCES) as [RemarkSource, ...RemarkSource[]]),
  kind: z.enum(Object.keys(REMARK_KINDS) as [RemarkKind, ...RemarkKind[]]),
  title: z.string().trim().min(3).max(300),
  description: z.string().trim().max(5000).nullable().optional(),
  /** Ссылка на норму: СП РК, СН РК, ГОСТ, пункт */
  normRef: z.string().trim().max(300).nullable().optional(),
  sheetId: z.uuid().nullable().optional(),
  /** Работа графика проекта, к которой относится замечание (ПР-07) */
  activityId: z.uuid().nullable().optional(),
  pointX: Point.nullable().optional(),
  pointY: Point.nullable().optional(),
  place: z.string().trim().max(300).nullable().optional(),
  contractorId: z.uuid().nullable().optional(),
  assigneeId: z.uuid().nullable().optional(),
  dueDate: DateStr,
  stopWork: z.boolean().optional(),
  /** Посещение журнала надзора, на котором выдано (АВ-01) */
  visitId: z.uuid().nullable().optional(),
  /** Внутреннее: внешние участники (подрядчик, заказчик) не видят (УЧ-07) */
  internal: z.boolean().optional(),
});
export type RemarkInput = z.infer<typeof RemarkInput>;

export const REMARK_ACTIONS = {
  take: 'Принять в работу',
  fix: 'Устранено — на проверку',
  close: 'Закрыть',
  return: 'Вернуть на устранение',
  dispute: 'Оспорить',
  cancel: 'Отменить',
  reopen: 'Открыть повторно',
  comment: 'Комментарий',
} as const;
export type RemarkAction = keyof typeof REMARK_ACTIONS;

export const RemarkActionInput = z.object({
  action: z.enum(Object.keys(REMARK_ACTIONS) as [RemarkAction, ...RemarkAction[]]),
  comment: z.string().trim().max(5000).nullable().optional(),
  rootCause: z
    .enum(Object.keys(ROOT_CAUSES) as [RootCause, ...RootCause[]])
    .nullable()
    .optional(),
  dueDate: DateStr.optional(),
  /** Проверено на посещении: закрыто или возвращено во время визита надзора (АВ-02) */
  visitId: z.uuid().nullable().optional(),
});
export type RemarkActionInput = z.infer<typeof RemarkActionInput>;

export const RemarkPhoto = z.object({
  id: z.uuid(),
  name: z.string(),
  stage: z.enum(['before', 'after', 'doc']),
  createdAt: z.string(),
});

export const RemarkSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  number: z.string(),
  source: z.enum(Object.keys(REMARK_SOURCES) as [RemarkSource, ...RemarkSource[]]),
  kind: z.enum(Object.keys(REMARK_KINDS) as [RemarkKind, ...RemarkKind[]]),
  title: z.string(),
  status: z.enum(Object.keys(REMARK_STATUSES) as [RemarkStatus, ...RemarkStatus[]]),
  overdue: z.boolean(),
  dueDate: z.string(),
  stopWork: z.boolean(),
  internal: z.boolean(),
  objectName: z.string().nullable(),
  setCode: z.string().nullable(),
  setId: z.uuid().nullable(),
  sheetNumber: z.string().nullable(),
  contractorName: z.string().nullable(),
  assigneeName: z.string().nullable(),
  issuedBy: z.string().nullable(),
  issuedAt: z.string(),
  closedAt: z.string().nullable(),
});
export type RemarkSummary = z.infer<typeof RemarkSummary>;

export const RemarkDetail = RemarkSummary.extend({
  description: z.string().nullable(),
  normRef: z.string().nullable(),
  place: z.string().nullable(),
  objectId: z.uuid().nullable(),
  sheetId: z.uuid().nullable(),
  activityId: z.uuid().nullable(),
  activityName: z.string().nullable(),
  revisionId: z.uuid().nullable(),
  revision: z.string().nullable(),
  pointX: z.number().nullable(),
  pointY: z.number().nullable(),
  contractorId: z.uuid().nullable(),
  assigneeId: z.uuid().nullable(),
  rootCause: z.string().nullable(),
  /** Проверить актуальность: лист перенесён на новую ревизию (ПД-26) */
  recheck: z.boolean(),
  visitId: z.uuid().nullable(),
  visitNumber: z.string().nullable(),
  photos: z.array(RemarkPhoto),
  /** Создано по пункту чек-листа качества (раздел 6.20): номер проверки, пункт и ссылка */
  checklist: z.object({ id: z.uuid(), number: z.string(), position: z.number().int() }).nullable(),
  events: z.array(
    z.object({
      at: z.string(),
      by: z.string().nullable(),
      action: z.string(),
      fromStatus: z.string().nullable(),
      toStatus: z.string().nullable(),
      comment: z.string().nullable(),
    }),
  ),
  /** Какие действия доступны этому человеку */
  actions: z.array(z.enum(Object.keys(REMARK_ACTIONS) as [RemarkAction, ...RemarkAction[]])),
});
export type RemarkDetail = z.infer<typeof RemarkDetail>;

export function remarkNumber(n: number): string {
  return `ЗАМ-${String(n).padStart(4, '0')}`;
}

/* ─────────── Вопросы к проекту (ВП-01…06) ─────────── */

/** Маршрут вопроса (ВП-02, УЧ-01): на проверке ПТО → у проектировщика → у заказчика → ответ принят */
export const QUESTION_STATUSES = {
  pto_review: 'На проверке ПТО',
  designer: 'У проектировщика',
  customer: 'У заказчика',
  answered: 'Ответ получен, ждёт ПТО',
  accepted: 'Ответ принят',
  merged: 'Объединён с другим',
  cancelled: 'Отменён',
} as const;
export type QuestionStatus = keyof typeof QUESTION_STATUSES;

export const OPEN_QUESTION: QuestionStatus[] = ['pto_review', 'designer', 'customer', 'answered'];

/** На чьей стороне вопрос (ВП-06) */
export const QUESTION_SIDE: Record<QuestionStatus, string | null> = {
  pto_review: 'ПТО',
  designer: 'ГИП проектировщика',
  customer: 'Заказчик',
  answered: 'ПТО',
  accepted: null,
  merged: null,
  cancelled: null,
};

export const QUESTION_KINDS = {
  question: 'Вопрос к проекту',
  proposal: 'Предложение по изменению проектного решения',
} as const;
export type QuestionKind = keyof typeof QUESTION_KINDS;

export const URGENCY = {
  normal: 'Обычный',
  urgent: 'Срочный',
  critical: 'Работы стоят',
} as const;
export type Urgency = keyof typeof URGENCY;

/** Срок ответа проектировщика по умолчанию, рабочих дней РК (ВП-04): уточняется в договоре */
export const DEFAULT_ANSWER_DAYS = 5;

export const QuestionInput = z.object({
  projectId: z.uuid(),
  kind: z.enum(['question', 'proposal']).optional(),
  objectId: z.uuid().nullable().optional(),
  sheetId: z.uuid().nullable().optional(),
  pointX: Point.nullable().optional(),
  pointY: Point.nullable().optional(),
  subject: z.string().trim().min(3).max(300),
  body: z.string().trim().min(3).max(10000),
  proposal: z.string().trim().max(5000).nullable().optional(),
  urgency: z.enum(['normal', 'urgent', 'critical']).optional(),
  blockedWorks: z.string().trim().max(1000).nullable().optional(),
  /** Срок ответа, рабочих дней РК (ВП-04) */
  answerDays: z.number().int().min(1).max(60).optional(),
  /** Уточняющий вопрос по тому же месту: номер с суффиксом ВП-004.1 (ВП-06) */
  parentId: z.uuid().nullable().optional(),
  /** Влияние предложения на срок, календарных дней (ВП-03) */
  scheduleDays: z.number().int().min(-3650).max(3650).nullable().optional(),
  reviewerIds: z.array(z.uuid()).max(20).optional(),
});
export type QuestionInput = z.infer<typeof QuestionInput>;

export const QUESTION_ACTIONS = {
  to_designer: 'Передать проектировщику',
  merge: 'Объединить с вопросом',
  answer: 'Ответить',
  to_customer: 'Передать заказчику',
  accept: 'Принять ответ',
  clarify: 'Задать уточнение',
  cancel: 'Отменить',
  comment: 'Комментарий',
} as const;
export type QuestionAction = keyof typeof QUESTION_ACTIONS;

export const QuestionActionInput = z.object({
  action: z.enum(Object.keys(QUESTION_ACTIONS) as [QuestionAction, ...QuestionAction[]]),
  comment: z.string().trim().max(10000).nullable().optional(),
  /** Ответ меняет проект — нужен запрос на изменение (ВП-02) */
  changesDesign: z.boolean().optional(),
  mergeIntoId: z.uuid().optional(),
  /** Принятый ответ — кому разослать (ВП-06) */
  notifyIds: z.array(z.uuid()).max(100).optional(),
});
export type QuestionActionInput = z.infer<typeof QuestionActionInput>;

export const QuestionSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  number: z.string(),
  kind: z.enum(['question', 'proposal']),
  subject: z.string(),
  status: z.enum(Object.keys(QUESTION_STATUSES) as [QuestionStatus, ...QuestionStatus[]]),
  side: z.string().nullable(),
  urgency: z.enum(['normal', 'urgent', 'critical']),
  dueDate: z.string().nullable(),
  overdue: z.boolean(),
  setCode: z.string().nullable(),
  setId: z.uuid().nullable(),
  sheetNumber: z.string().nullable(),
  authorName: z.string().nullable(),
  createdAt: z.string(),
  answeredAt: z.string().nullable(),
  changesDesign: z.boolean(),
});
export type QuestionSummary = z.infer<typeof QuestionSummary>;

export const QuestionDetail = QuestionSummary.extend({
  /** Работы графика, которые ждут ответа (ВП-04); null — человек не видит график */
  works: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string() })).nullable(),
  /** Напоминания по сроку (ВП-04): 0 — нет, 1 — напомнили, 2 — просрочен, 3 — эскалация ГИП */
  reminderStage: z.number().int(),
  canLinkWorks: z.boolean(),
  body: z.string(),
  proposal: z.string().nullable(),
  blockedWorks: z.string().nullable(),
  answer: z.string().nullable(),
  answeredBy: z.string().nullable(),
  objectId: z.uuid().nullable(),
  objectName: z.string().nullable(),
  sheetId: z.uuid().nullable(),
  revision: z.string().nullable(),
  pointX: z.number().nullable(),
  pointY: z.number().nullable(),
  scheduleDays: z.number().nullable(),
  parentId: z.uuid().nullable(),
  parentNumber: z.string().nullable(),
  mergedInto: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  children: z.array(z.object({ id: z.uuid(), number: z.string(), status: z.string() })),
  reviewers: z.array(z.object({ id: z.uuid(), fullName: z.string() })),
  recheck: z.boolean(),
  photos: z.array(RemarkPhoto),
  events: z.array(
    z.object({
      at: z.string(),
      by: z.string().nullable(),
      action: z.string(),
      fromStatus: z.string().nullable(),
      toStatus: z.string().nullable(),
      comment: z.string().nullable(),
    }),
  ),
  actions: z.array(z.enum(Object.keys(QUESTION_ACTIONS) as [QuestionAction, ...QuestionAction[]])),
});
export type QuestionDetail = z.infer<typeof QuestionDetail>;

/** ВП-004, уточнение — ВП-004.1 (ВП-06) */
export function questionNumber(n: number, sub: number | null = null): string {
  return `ВП-${String(n).padStart(3, '0')}${sub ? `.${sub}` : ''}`;
}

/** Сроки и просрочки по источникам и ответственным (УЧ-02) */
export const SupervisionStats = z.object({
  remarks: z.object({ open: z.number(), overdue: z.number(), stopWork: z.number() }),
  questions: z.object({ open: z.number(), overdue: z.number() }),
  bySource: z.array(
    z.object({
      source: z.string(),
      open: z.number(),
      overdue: z.number(),
      closed: z.number(),
      /** Средний срок устранения, дней */
      avgFixDays: z.number().nullable(),
    }),
  ),
  byContractor: z.array(
    z.object({ name: z.string(), open: z.number(), overdue: z.number(), avgFixDays: z.number().nullable() }),
  ),
  /** Средний срок ответа проектировщика, рабочих дней */
  avgAnswerDays: z.number().nullable(),
});
export type SupervisionStats = z.infer<typeof SupervisionStats>;

/* ─────────── Рабочие дни Республики Казахстан (ВП-04) ─────────── */

/**
 * Праздничные дни РК (Закон «О праздниках в РК»): 1–2 января, 7 января, 8 марта, 21–23 марта, 1, 7 и 9 мая,
 * 6 июля, 30 августа, 25 октября, 16 декабря и первый день Курбан айта. Праздник в выходной переносится на
 * следующий рабочий день. Переносы выходных постановлением правительства добавляются в EXTRA_HOLIDAYS.
 */
const FIXED_HOLIDAYS = [
  '01-01',
  '01-02',
  '01-07',
  '03-08',
  '03-21',
  '03-22',
  '03-23',
  '05-01',
  '05-07',
  '05-09',
  '07-06',
  '08-30',
  '10-25',
  '12-16',
];
const KURBAN_AIT: Record<number, string> = { 2025: '06-06', 2026: '05-27', 2027: '05-16', 2028: '05-05' };
const EXTRA_HOLIDAYS = new Set<string>([]);

const holidayCache = new Map<number, Set<string>>();

function holidaysOf(year: number): Set<string> {
  const hit = holidayCache.get(year);
  if (hit) return hit;
  const set = new Set<string>();
  const days = [...FIXED_HOLIDAYS, ...(KURBAN_AIT[year] ? [KURBAN_AIT[year]] : [])];
  for (const md of days) {
    const d = new Date(`${year}-${md}T00:00:00Z`);
    set.add(d.toISOString().slice(0, 10));
  }
  // Праздник в субботу или воскресенье — выходной в следующий рабочий день (кроме Курбан айта и 7 января)
  for (const md of FIXED_HOLIDAYS.filter((x) => x !== '01-07')) {
    const d = new Date(`${year}-${md}T00:00:00Z`);
    const wd = d.getUTCDay();
    if (wd === 0 || wd === 6) {
      const n = new Date(d);
      do n.setUTCDate(n.getUTCDate() + 1);
      while (n.getUTCDay() === 0 || n.getUTCDay() === 6 || set.has(n.toISOString().slice(0, 10)));
      set.add(n.toISOString().slice(0, 10));
    }
  }
  for (const x of EXTRA_HOLIDAYS) if (x.startsWith(String(year))) set.add(x);
  holidayCache.set(year, set);
  return set;
}

export function isWorkingDayKz(day: string): boolean {
  const d = new Date(`${day}T00:00:00Z`);
  const wd = d.getUTCDay();
  if (wd === 0 || wd === 6) return false;
  return !holidaysOf(d.getUTCFullYear()).has(day);
}

/** Дата через n рабочих дней РК после from (ГГГГ-ММ-ДД) */
export function addWorkingDaysKz(from: string, n: number): string {
  const d = new Date(`${from}T00:00:00Z`);
  let left = n;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (isWorkingDayKz(d.toISOString().slice(0, 10))) left--;
  }
  return d.toISOString().slice(0, 10);
}

/** Сколько рабочих дней РК между датами (from не считается, to считается) */
export function workingDaysBetweenKz(from: string, to: string): number {
  if (to <= from) return 0;
  const d = new Date(`${from}T00:00:00Z`);
  let n = 0;
  while (d.toISOString().slice(0, 10) < to) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (isWorkingDayKz(d.toISOString().slice(0, 10))) n++;
  }
  return n;
}

/* ─────────── Журнал посещений авторского и технического надзора (АВ-01, АВ-02) ─────────── */

/** Чей журнал: посещения ведёт надзор, замечания которого привязаны к визиту */
export const VISIT_KINDS = {
  author_supervision: 'Авторский надзор',
  tech_supervision: 'Технический надзор',
} as const;
export type VisitKind = keyof typeof VISIT_KINDS;

export const VISIT_STATUSES = { open: 'Идёт', closed: 'Запись подписана' } as const;
export type VisitStatus = keyof typeof VISIT_STATUSES;

export const VisitInput = z.object({
  projectId: z.uuid(),
  objectId: z.uuid().nullable().optional(),
  kind: z.enum(Object.keys(VISIT_KINDS) as [VisitKind, ...VisitKind[]]),
  visitDate: DateStr,
  /** Проектная организация или организация технадзора */
  organizationId: z.uuid().nullable().optional(),
  /** Кто был на объекте: участники проекта */
  participantIds: z.array(z.uuid()).max(30).default([]),
  purpose: z.string().trim().max(500).nullable().optional(),
  /** Запись в журнале: что осмотрено, что установлено */
  notes: z.string().trim().max(10000).nullable().optional(),
});
export type VisitInput = z.input<typeof VisitInput>;

export const VisitPatch = z.object({
  objectId: z.uuid().nullable().optional(),
  visitDate: DateStr.optional(),
  organizationId: z.uuid().nullable().optional(),
  participantIds: z.array(z.uuid()).max(30).optional(),
  purpose: z.string().trim().max(500).nullable().optional(),
  notes: z.string().trim().max(10000).nullable().optional(),
});
export type VisitPatch = z.infer<typeof VisitPatch>;

export const VisitSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  number: z.string(),
  kind: z.enum(Object.keys(VISIT_KINDS) as [VisitKind, ...VisitKind[]]),
  visitDate: z.string(),
  objectName: z.string().nullable(),
  organizationName: z.string().nullable(),
  participants: z.array(z.string()),
  status: z.enum(Object.keys(VISIT_STATUSES) as [VisitStatus, ...VisitStatus[]]),
  issued: z.number(),
  checked: z.number(),
  createdBy: z.string().nullable(),
});
export type VisitSummary = z.infer<typeof VisitSummary>;

export const VISIT_ACTIONS = {
  edit: 'Изменить',
  close: 'Подписать запись',
  reopen: 'Открыть запись',
} as const;
export type VisitAction = keyof typeof VISIT_ACTIONS;

export const VisitDetail = VisitSummary.extend({
  objectId: z.uuid().nullable(),
  organizationId: z.uuid().nullable(),
  participantIds: z.array(z.uuid()),
  purpose: z.string().nullable(),
  notes: z.string().nullable(),
  closedAt: z.string().nullable(),
  closedBy: z.string().nullable(),
  /** Выдано на этом посещении */
  remarks: z.array(RemarkSummary),
  /** Открытые замечания этого надзора с прошлых посещений — проверить и закрыть или вернуть (АВ-02) */
  toCheck: z.array(RemarkSummary),
  /** Что проверено на этом посещении */
  checkedItems: z.array(
    z.object({
      remarkId: z.uuid(),
      number: z.string(),
      title: z.string(),
      action: z.string(),
      comment: z.string().nullable(),
      at: z.string(),
      by: z.string().nullable(),
    }),
  ),
  actions: z.array(z.enum(Object.keys(VISIT_ACTIONS) as [VisitAction, ...VisitAction[]])),
  /** Можно выдавать замечания и отмечать проверку на этом посещении */
  canIssue: z.boolean(),
});
export type VisitDetail = z.infer<typeof VisitDetail>;

export function visitNumber(kind: VisitKind, n: number): string {
  return `${kind === 'author_supervision' ? 'АН' : 'ТНВ'}-${String(n).padStart(3, '0')}`;
}

/* ─────────── Освидетельствование скрытых работ (ТН-03) ─────────── */

export const INSPECTION_STATUSES = {
  called: 'Вызов отправлен',
  accepted: 'Принято, акт подписан',
  rejected: 'Отказ в приёмке',
  cancelled: 'Вызов отменён',
} as const;
export type InspectionStatus = keyof typeof INSPECTION_STATUSES;

export const INSPECTION_ACTIONS = {
  accept: 'Принять и подписать акт',
  reject: 'Отказать',
  reschedule: 'Перенести',
  cancel: 'Отменить вызов',
  comment: 'Комментарий',
} as const;
export type InspectionAction = keyof typeof INSPECTION_ACTIONS;

/** Чек-лист приёмки по умолчанию: подрядчик правит его при вызове */
export const DEFAULT_INSPECTION_CHECKLIST = [
  'Работы выполнены по рабочей документации действующей ревизии',
  'Применённые материалы имеют паспорта и сертификаты',
  'Исполнительная схема с отметками приложена',
  'Отклонения в пределах допусков СП РК',
  'Фото работ до закрытия приложены',
];

export const InspectionInput = z.object({
  projectId: z.uuid(),
  objectId: z.uuid().nullable().optional(),
  /** Какие скрытые работы предъявляются */
  workName: z.string().trim().min(3).max(300),
  /** Работа графика, которую предъявляют, и следующая — её нельзя начинать без подписанного акта */
  activityId: z.uuid().nullable().optional(),
  nextActivityId: z.uuid().nullable().optional(),
  sheetId: z.uuid().nullable().optional(),
  /** Отчёт смены, из которого предъявляются скрытые работы (ПР-10) */
  reportId: z.uuid().nullable().optional(),
  /**
   * При вызове из отчёта приложить фото этой работы с `fileId` в акт (ПР-10).
   * По умолчанию да, если указан reportId; false — только ссылка на отчёт без копирования фото.
   */
  attachReportPhotos: z.boolean().optional(),
  place: z.string().trim().max(300).nullable().optional(),
  /** Дата и время, к которым приглашают на объект */
  scheduledAt: z.iso.datetime({ offset: true }),
  /** Кого вызывают: технадзор, авторский надзор */
  inspectorIds: z.array(z.uuid()).min(1).max(10),
  contractorId: z.uuid().nullable().optional(),
  checklist: z.array(z.string().trim().min(2).max(300)).min(1).max(50),
});
export type InspectionInput = z.input<typeof InspectionInput>;

export const InspectionActionInput = z
  .object({
    action: z.enum(Object.keys(INSPECTION_ACTIONS) as [InspectionAction, ...InspectionAction[]]),
    comment: z.string().trim().max(5000).nullable().optional(),
    /** Отметки по пунктам чек-листа: при приёмке — все пункты выполнены */
    checks: z
      .array(z.object({ ok: z.boolean(), note: z.string().trim().max(500).nullable().optional() }))
      .max(50)
      .optional(),
    scheduledAt: z.iso.datetime({ offset: true }).optional(),
    /** Какие шаги «акт» предъявляемой работы подтверждены; при нескольких шагах выбор обязателен. */
    stepIds: z
      .array(z.uuid())
      .min(1)
      .max(50)
      .refine((ids) => new Set(ids).size === ids.length, 'Шаги не должны повторяться')
      .optional(),
    /** Принять только документ: сервер повторно проверяет, что все актовые шаги уже закрыты. */
    stepsAlreadyClosed: z.literal(true).optional(),
  })
  .refine((body) => body.stepIds === undefined || body.action === 'accept', {
    message: 'Шаги указываются только при принятии акта',
    path: ['stepIds'],
  })
  .refine((body) => !body.stepsAlreadyClosed || (body.action === 'accept' && body.stepIds === undefined), {
    message: 'Приёмка без новых шагов допустима только для accept без stepIds',
    path: ['stepsAlreadyClosed'],
  });
export type InspectionActionInput = z.infer<typeof InspectionActionInput>;

export const InspectionCheck = z.object({
  text: z.string(),
  ok: z.boolean().nullable(),
  note: z.string().nullable(),
});
export type InspectionCheck = z.infer<typeof InspectionCheck>;

export const InspectionSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  number: z.string(),
  workName: z.string(),
  status: z.enum(Object.keys(INSPECTION_STATUSES) as [InspectionStatus, ...InspectionStatus[]]),
  scheduledAt: z.string(),
  objectName: z.string().nullable(),
  activityId: z.uuid().nullable(),
  activityName: z.string().nullable(),
  nextActivityName: z.string().nullable(),
  inspectors: z.array(z.string()),
  contractorName: z.string().nullable(),
  createdBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
});
export type InspectionSummary = z.infer<typeof InspectionSummary>;

export const InspectionDetail = InspectionSummary.extend({
  /** Отчёт смены, из которого вызвали на освидетельствование (ПР-10); дата и смена — только сотрудникам */
  report: z.object({ id: z.uuid(), reportDay: z.string(), shiftKey: z.string() }).nullable(),
  objectId: z.uuid().nullable(),
  activityId: z.uuid().nullable(),
  nextActivityId: z.uuid().nullable(),
  sheetId: z.uuid().nullable(),
  setId: z.uuid().nullable(),
  setCode: z.string().nullable(),
  sheetNumber: z.string().nullable(),
  place: z.string().nullable(),
  inspectorIds: z.array(z.uuid()),
  contractorId: z.uuid().nullable(),
  checklist: z.array(InspectionCheck),
  decidedBy: z.string().nullable(),
  decision: z.string().nullable(),
  photos: z.array(RemarkPhoto),
  events: z.array(
    z.object({
      at: z.string(),
      by: z.string().nullable(),
      action: z.string(),
      comment: z.string().nullable(),
    }),
  ),
  actions: z.array(z.enum(Object.keys(INSPECTION_ACTIONS) as [InspectionAction, ...InspectionAction[]])),
});
export type InspectionDetail = z.infer<typeof InspectionDetail>;

/** Только актовые шаги предъявляемой работы, доступные назначенному принимающему. */
export const InspectionStepOptions = z.object({
  activityId: z.uuid().nullable(),
  steps: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      weight: z.number(),
      doneAt: z.string().nullable(),
    }),
  ),
});
export type InspectionStepOptions = z.infer<typeof InspectionStepOptions>;

export function inspectionNumber(n: number): string {
  return `АОСР-${String(n).padStart(3, '0')}`;
}

/* ─────────── Протокол совещания и отчёт для заказчика (УЧ-04) ─────────── */

export const SupervisionReport = z.object({
  projectId: z.uuid(),
  projectCode: z.string(),
  projectName: z.string(),
  /** Дата отчёта: состояние замечаний и вопросов на конец этого дня */
  date: z.string(),
  /** Начало периода решений */
  from: z.string(),
  internal: z.boolean(),
  remarks: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      source: z.string(),
      title: z.string(),
      where: z.string().nullable(),
      contractor: z.string().nullable(),
      assignee: z.string().nullable(),
      dueDate: z.string(),
      overdue: z.boolean(),
      stopWork: z.boolean(),
      status: z.string(),
    }),
  ),
  questions: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      subject: z.string(),
      where: z.string().nullable(),
      author: z.string().nullable(),
      dueDate: z.string().nullable(),
      overdue: z.boolean(),
      critical: z.boolean(),
      status: z.string(),
    }),
  ),
  decisions: z.array(
    z.object({
      kind: z.enum(['question', 'remark']),
      id: z.uuid(),
      number: z.string(),
      title: z.string(),
      decision: z.string(),
      at: z.string(),
    }),
  ),
  tasks: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      assignee: z.string().nullable(),
      dueDate: z.string().nullable(),
      overdue: z.boolean(),
      link: z.string().nullable(),
    }),
  ),
});
export type SupervisionReport = z.infer<typeof SupervisionReport>;

/* ─────────── Настройки надзора компании (УЧ-07) ─────────── */

export const SUPERVISION_SETTINGS_KEY = 'supervision.settings';

/** Какие источники замечаний внутренние: их не видят внешние участники (подрядчик, технадзор, проектировщик) */
export const SupervisionSettings = z.object({
  internalSources: z.array(z.enum(Object.keys(REMARK_SOURCES) as [RemarkSource, ...RemarkSource[]])).max(20),
});
export type SupervisionSettings = z.infer<typeof SupervisionSettings>;
export const SUPERVISION_DEFAULTS: SupervisionSettings = { internalSources: INTERNAL_SOURCES };
